import { createRoute, Outlet, redirect } from '@tanstack/react-router';
import { z } from 'zod';
import {
  getGetMeQueryOptions,
  BulkReprocessRunDtoStatus,
  BulkReprocessRunDtoMode,
} from '@sara/api-client';
import Session from 'supertokens-auth-react/recipe/session';
import { AppLayout } from '../views/_layout/app-layout';
import { AdminLayout } from '../views/_layout/admin-layout';
import { AppLoadingScreen } from '../views/_layout/app-loading-screen';
import { AuthPage } from '../views/auth/auth-page';
import { ConsentPage } from '../views/auth/consent-page';
import { OrgSelectorPage } from '../views/orgs/org-selector-page';
import { InterviewDetailPage } from '../views/interviews/interview-detail-page';
import { InterviewsPage } from '../views/interviews/interviews-page';
import { InterviewTypeCreatePage } from '../views/interview-types/interview-type-create-page';
import { InterviewTypeDetailPage } from '../views/interview-types/interview-type-detail-page';
import { InterviewTypeEditPage } from '../views/interview-types/interview-type-edit-page';
import { InterviewTypesPage } from '../views/interview-types/interview-types-page';
import { CandidatesPage } from '../views/candidates/candidates-page';
import { CandidateDetailPage } from '../views/candidates/candidate-detail-page';
import { ApiKeysSettingsPage } from '../views/settings/api-keys-page';
import { TeamSettingsPage } from '../views/settings/team-page';
import { BrandingSettingsPage } from '../views/settings/branding-page';
import { DomainsSettingsPage } from '../views/settings/domains-page';
import { WebhooksSettingsPage } from '../views/settings/webhooks-page';
import { RubricsPage } from '../views/rubrics/rubrics-page';
import { RubricCreatePage } from '../views/rubrics/rubric-create-page';
import { RubricDetailPage } from '../views/rubrics/rubric-detail-page';
import { RubricEditPage } from '../views/rubrics/rubric-edit-page';
import { ExtractionsPage } from '../views/extractions/extractions-page';
import { ExtractionCreatePage } from '../views/extractions/extraction-create-page';
import { ExtractionDetailPage } from '../views/extractions/extraction-detail-page';
import { ExtractionEditPage } from '../views/extractions/extraction-edit-page';
import { BulkReprocessRunsPage } from '../views/admin/bulk-reprocess-runs-page';
import { BulkReprocessNewPage } from '../views/admin/bulk-reprocess-new-page';
import { BulkReprocessRunPage } from '../views/admin/bulk-reprocess-run-page';
import { rootRoute } from './root';
import { decideOrgEntry } from '../lib/org-entry';
import { canAccessAdmin } from '../lib/admin-gate';
import { readLastOrgSlug, writeLastOrgSlug } from '../lib/last-org';

// ---------------------------------------------------------------------------
// Search schemas (unchanged)
// ---------------------------------------------------------------------------

const INTERVIEW_STATUSES = [
  'PENDING',
  'IN_PROGRESS',
  'COMPLETED',
  'PAUSED',
  'EXHAUSTED',
  'ERROR',
  'EXPIRED',
  'LIVE_INTERVIEW_REQUESTED',
] as const;

export const INTERVIEW_SORT_FIELDS = ['createdAt', 'expiresAt'] as const;
export type InterviewSortField = (typeof INTERVIEW_SORT_FIELDS)[number];

export const CANDIDATE_SORT_FIELDS = ['name', 'createdAt'] as const;
export type CandidateSortField = (typeof CANDIDATE_SORT_FIELDS)[number];

export const candidatesSearchSchema = z.object({
  search: z.string().min(1).max(100).optional(),
  sortBy: z.enum(CANDIDATE_SORT_FIELDS).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.union([z.literal(10), z.literal(25), z.literal(50), z.literal(100)]).default(25),
});

export type CandidatesSearch = z.infer<typeof candidatesSearchSchema>;

export const candidatesHomeSearch: CandidatesSearch = {
  sortBy: 'createdAt',
  sortOrder: 'desc',
  page: 1,
  pageSize: 25,
};

export const interviewsSearchSchema = z.object({
  status: z.array(z.enum(INTERVIEW_STATUSES)).optional(),
  candidateSearch: z.string().min(1).max(100).optional(),
  externalUserIdSearch: z.string().min(1).max(100).optional(),
  interviewTypeVersionId: z.string().uuid().optional(),
  createdAfter: z.string().datetime({ offset: true }).optional(),
  createdBefore: z.string().datetime({ offset: true }).optional(),
  expiresAfter: z.string().datetime({ offset: true }).optional(),
  expiresBefore: z.string().datetime({ offset: true }).optional(),
  completedNoResults: z.boolean().optional(),
  sortBy: z.enum(INTERVIEW_SORT_FIELDS).default('createdAt'),
  sortOrder: z.enum(['asc', 'desc']).default('desc'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.union([z.literal(10), z.literal(25), z.literal(50), z.literal(100)]).default(25),
});

export type InterviewsSearch = z.infer<typeof interviewsSearchSchema>;

export const interviewsHomeSearch: InterviewsSearch = {
  sortBy: 'createdAt',
  sortOrder: 'desc',
  page: 1,
  pageSize: 25,
};

export const rubricCreateSearchSchema = z.object({
  // .catch: a malformed forkFrom (e.g. a truncated shared link) degrades to a
  // blank create form instead of erroring the whole route.
  forkFrom: z.string().uuid().optional().catch(undefined),
});

// Runs-list filters + pagination (deep-linkable). `.catch` degrades a
// malformed/stale value to "unset" instead of erroring the whole route.
export const bulkReprocessRunsSearchSchema = z.object({
  status: z.nativeEnum(BulkReprocessRunDtoStatus).optional().catch(undefined),
  mode: z.nativeEnum(BulkReprocessRunDtoMode).optional().catch(undefined),
  page: z.number().int().min(1).optional().catch(undefined),
});

export type BulkReprocessRunsSearch = z.infer<typeof bulkReprocessRunsSearchSchema>;

// Prefill params for the launch form's filter fields (the `/new` route) —
// carried over from the sessions list's "Replay Completed Sessions" hand-off
// (see InterviewListContainerContent). Field names mirror BulkReprocessFilterDto
// (e.g. `externalUserId`, not the sessions list's `externalUserIdSearch`).
// `.catch`: a malformed/stale value degrades to an unseeded field instead of
// erroring the whole route.
export const bulkReprocessSearchSchema = z.object({
  // Marks arrival from the sessions list's "Replay Completed Sessions" action;
  // the resolved session ids come through sessionStorage (see replay-handoff),
  // not the URL, since there can be thousands.
  handoff: z.literal('sessions').optional().catch(undefined),
  orgId: z.string().uuid().optional().catch(undefined),
  completedNoResults: z.boolean().optional().catch(undefined),
  interviewTypeVersionId: z.string().uuid().optional().catch(undefined),
  externalUserId: z.string().min(1).max(100).optional().catch(undefined),
  createdAfter: z.string().datetime({ offset: true }).optional().catch(undefined),
  createdBefore: z.string().datetime({ offset: true }).optional().catch(undefined),
});

export type BulkReprocessSearch = z.infer<typeof bulkReprocessSearchSchema>;

// ---------------------------------------------------------------------------
// Auth routes (unauthenticated)
// ---------------------------------------------------------------------------

const authLayoutRoute = createRoute({
  path: 'auth',
  getParentRoute: () => rootRoute,
  beforeLoad: async () => {
    let hasSession = false;
    try {
      hasSession = await Session.doesSessionExist();
    } catch {
      // Session check failed — treat as unauthenticated
    }
    if (hasSession) throw redirect({ to: '/orgs' });
  },
  component: () => <Outlet />,
});

const authIndexRoute = createRoute({
  path: '/',
  getParentRoute: () => authLayoutRoute,
  component: AuthPage,
});

const authSplatRoute = createRoute({
  path: '$',
  getParentRoute: () => authLayoutRoute,
  component: AuthPage,
});

const authRouteTree = authLayoutRoute.addChildren([authIndexRoute, authSplatRoute]);

// OAuth2 consent page. A top-level route (NOT under authLayoutRoute) because
// consent happens WHILE the user is logged in — authLayoutRoute.beforeLoad
// redirects logged-in users away from /auth, which would break the flow. The
// static path out-ranks the /auth/$ splat, so this wins for /auth/oauth/consent.
const oauthConsentRoute = createRoute({
  path: 'auth/oauth/consent',
  getParentRoute: () => rootRoute,
  validateSearch: (search: Record<string, unknown>): { consentChallenge?: string } => ({
    consentChallenge:
      typeof search.consentChallenge === 'string' ? search.consentChallenge : undefined,
  }),
  component: ConsentPage,
});

// ---------------------------------------------------------------------------
// Authenticated gate (no chrome)
// ---------------------------------------------------------------------------

const authenticatedRoute = createRoute({
  id: 'authenticated',
  getParentRoute: () => rootRoute,
  beforeLoad: async ({ location, context }) => {
    const hasSession = await Session.doesSessionExist();
    if (!hasSession) {
      throw redirect({ to: '/auth', search: { returnTo: location.href } });
    }
    await context.queryClient.ensureQueryData(getGetMeQueryOptions());
  },
  pendingMs: 0,
  pendingComponent: AppLoadingScreen,
  component: () => <Outlet />,
});

// `/` redirects into `/orgs` so the entry decision lives in one place.
const homeRedirectRoute = createRoute({
  path: '/',
  getParentRoute: () => authenticatedRoute,
  beforeLoad: () => {
    throw redirect({ to: '/orgs' });
  },
});

// ---------------------------------------------------------------------------
// Admin (staff-only) — `/admin` (no chrome — gated on isSuperAdmin)
// ---------------------------------------------------------------------------

const adminRoute = createRoute({
  path: 'admin',
  getParentRoute: () => authenticatedRoute,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getGetMeQueryOptions());
    if (!canAccessAdmin(me)) {
      // Not staff — bounce to the same entry point a non-member org redirect
      // uses, rather than a dead end.
      throw redirect({ to: '/orgs' });
    }
  },
  component: AdminLayout,
});

// `/admin` has no landing of its own yet — Bulk Replay is the only tool — so
// the index redirects there rather than rendering an empty shell.
const adminIndexRoute = createRoute({
  path: '/',
  getParentRoute: () => adminRoute,
  beforeLoad: () => {
    throw redirect({ to: '/admin/bulk-replay' });
  },
});

// Routes live under `bulk-replay` (the user-facing vocabulary); the code
// identifiers stay `bulkReprocess*` / `BulkReprocess*` since the API + DB layer
// is still "reprocess".
const bulkReprocessRoute = createRoute({
  path: 'bulk-replay',
  getParentRoute: () => adminRoute,
  component: BulkReprocessRunsPage,
  validateSearch: (input) => bulkReprocessRunsSearchSchema.parse(input),
});

// Static `new` is declared alongside dynamic `$runId`; the router matches the
// static segment first, so `/bulk-replay/new` never resolves as a run id.
const bulkReprocessNewRoute = createRoute({
  path: 'bulk-replay/new',
  getParentRoute: () => adminRoute,
  component: BulkReprocessNewPage,
  validateSearch: (input) => bulkReprocessSearchSchema.parse(input),
});

const bulkReprocessRunRoute = createRoute({
  path: 'bulk-replay/$runId',
  getParentRoute: () => adminRoute,
  component: BulkReprocessRunPage,
});

const adminRouteTree = adminRoute.addChildren([
  adminIndexRoute,
  bulkReprocessRoute,
  bulkReprocessNewRoute,
  bulkReprocessRunRoute,
]);

// ---------------------------------------------------------------------------
// Orgs layout — `/orgs` (no chrome — selector renders its own page)
// ---------------------------------------------------------------------------

const orgsLayoutRoute = createRoute({
  path: 'orgs',
  getParentRoute: () => authenticatedRoute,
  component: () => <Outlet />,
});

// `/orgs` — entry decision: redirect to last/only org, else show selector.
const orgsIndexRoute = createRoute({
  path: '/',
  getParentRoute: () => orgsLayoutRoute,
  beforeLoad: async ({ context }) => {
    const me = await context.queryClient.ensureQueryData(getGetMeQueryOptions());
    const decision = decideOrgEntry({
      memberships: me.memberships,
      lastSlug: readLastOrgSlug(),
    });
    if (decision.kind === 'redirect') {
      throw redirect({
        to: '/orgs/$orgSlug/sessions',
        params: { orgSlug: decision.slug },
        search: interviewsHomeSearch,
      });
    }
  },
  component: OrgSelectorPage,
});

// ---------------------------------------------------------------------------
// Org-scoped layout — `/orgs/$orgSlug` (full app chrome)
// ---------------------------------------------------------------------------

const orgRoute = createRoute({
  path: '$orgSlug',
  getParentRoute: () => orgsLayoutRoute,
  beforeLoad: async ({ params, context }) => {
    const me = await context.queryClient.ensureQueryData(getGetMeQueryOptions());
    const membership = me.memberships.find((m) => m.organization.slug === params.orgSlug);
    if (!membership) {
      // User isn't (or no longer is) a member — bounce to selector.
      throw redirect({ to: '/orgs' });
    }
    writeLastOrgSlug(membership.organization.slug);
    return { membership };
  },
  component: AppLayout,
});

const interviewsCrumb = { label: 'Interviews' } as const;
const sessionsCrumb = {
  label: 'Sessions',
  to: '/orgs/:orgSlug/sessions',
  parent: interviewsCrumb,
} as const;
const templatesCrumb = {
  label: 'Templates',
  to: '/orgs/:orgSlug/templates',
  parent: interviewsCrumb,
} as const;
const candidatesCrumb = { label: 'Candidates', to: '/orgs/:orgSlug/candidates' } as const;
const rubricsCrumb = {
  label: 'Rubrics',
  to: '/orgs/:orgSlug/rubrics',
  parent: interviewsCrumb,
} as const;
const extractionsCrumb = {
  label: 'Extractions',
  to: '/orgs/:orgSlug/extractions',
  parent: interviewsCrumb,
} as const;

const sessionsRoute = createRoute({
  path: 'sessions',
  getParentRoute: () => orgRoute,
  component: InterviewsPage,
  validateSearch: (input) => interviewsSearchSchema.parse(input),
  staticData: { crumb: { kind: 'static', ...sessionsCrumb } },
});

const sessionDetailRoute = createRoute({
  path: 'sessions/$interviewId',
  getParentRoute: () => orgRoute,
  component: InterviewDetailPage,
  staticData: { crumb: { kind: 'interview', parent: sessionsCrumb } },
});

const templatesRoute = createRoute({
  path: 'templates',
  getParentRoute: () => orgRoute,
  component: InterviewTypesPage,
  staticData: { crumb: { kind: 'static', ...templatesCrumb } },
});

const templateCreateRoute = createRoute({
  path: 'templates/new',
  getParentRoute: () => orgRoute,
  component: InterviewTypeCreatePage,
  staticData: {
    crumb: { kind: 'static', label: 'New template', parent: templatesCrumb },
  },
});

const templateDetailLayoutRoute = createRoute({
  path: 'templates/$interviewTypeId',
  getParentRoute: () => orgRoute,
  component: () => <Outlet />,
  staticData: { crumb: { kind: 'interviewType', parent: templatesCrumb } },
});

const templateDetailIndexRoute = createRoute({
  path: '/',
  getParentRoute: () => templateDetailLayoutRoute,
  component: InterviewTypeDetailPage,
});

const templateEditRoute = createRoute({
  path: 'edit',
  getParentRoute: () => templateDetailLayoutRoute,
  component: InterviewTypeEditPage,
  staticData: {
    crumb: { kind: 'static', label: 'New version' },
    // Two-column editor (prompt + config) — needs more room than the default column.
    wide: true,
  },
});

const templateDetailRouteTree = templateDetailLayoutRoute.addChildren([
  templateDetailIndexRoute,
  templateEditRoute,
]);

const rubricsRoute = createRoute({
  path: 'rubrics',
  getParentRoute: () => orgRoute,
  component: RubricsPage,
  staticData: { crumb: { kind: 'static', ...rubricsCrumb } },
});

const rubricCreateRoute = createRoute({
  path: 'rubrics/new',
  getParentRoute: () => orgRoute,
  component: RubricCreatePage,
  validateSearch: (input) => rubricCreateSearchSchema.parse(input),
  staticData: {
    crumb: { kind: 'static', label: 'New rubric', parent: rubricsCrumb },
  },
});

const rubricDetailLayoutRoute = createRoute({
  path: 'rubrics/$rubricId',
  getParentRoute: () => orgRoute,
  component: () => <Outlet />,
  staticData: { crumb: { kind: 'static', label: 'Rubric', parent: rubricsCrumb } },
});

const rubricDetailIndexRoute = createRoute({
  path: '/',
  getParentRoute: () => rubricDetailLayoutRoute,
  component: RubricDetailPage,
});

const rubricEditRoute = createRoute({
  path: 'edit',
  getParentRoute: () => rubricDetailLayoutRoute,
  component: RubricEditPage,
  staticData: {
    crumb: { kind: 'static', label: 'New version' },
  },
});

const rubricDetailRouteTree = rubricDetailLayoutRoute.addChildren([
  rubricDetailIndexRoute,
  rubricEditRoute,
]);

const extractionsRoute = createRoute({
  path: 'extractions',
  getParentRoute: () => orgRoute,
  component: ExtractionsPage,
  staticData: { crumb: { kind: 'static', ...extractionsCrumb } },
});

const extractionCreateRoute = createRoute({
  path: 'extractions/new',
  getParentRoute: () => orgRoute,
  component: ExtractionCreatePage,
  staticData: {
    crumb: { kind: 'static', label: 'New extraction', parent: extractionsCrumb },
  },
});

const extractionDetailLayoutRoute = createRoute({
  path: 'extractions/$extractionId',
  getParentRoute: () => orgRoute,
  component: () => <Outlet />,
  staticData: { crumb: { kind: 'static', label: 'Extraction', parent: extractionsCrumb } },
});

const extractionDetailIndexRoute = createRoute({
  path: '/',
  getParentRoute: () => extractionDetailLayoutRoute,
  component: ExtractionDetailPage,
});

const extractionEditRoute = createRoute({
  path: 'edit',
  getParentRoute: () => extractionDetailLayoutRoute,
  component: ExtractionEditPage,
  staticData: {
    crumb: { kind: 'static', label: 'New version' },
  },
});

const extractionDetailRouteTree = extractionDetailLayoutRoute.addChildren([
  extractionDetailIndexRoute,
  extractionEditRoute,
]);

const candidatesIndexRoute = createRoute({
  path: 'candidates',
  getParentRoute: () => orgRoute,
  component: CandidatesPage,
  validateSearch: (input) => candidatesSearchSchema.parse(input),
  staticData: { crumb: { kind: 'static', ...candidatesCrumb } },
});

const candidateDetailRoute = createRoute({
  path: 'candidates/$candidateId',
  getParentRoute: () => orgRoute,
  component: CandidateDetailPage,
  staticData: { crumb: { kind: 'candidate', parent: candidatesCrumb } },
});

const settingsLayoutRoute = createRoute({
  path: 'settings',
  getParentRoute: () => orgRoute,
  component: () => <Outlet />,
  staticData: {
    crumb: { kind: 'static', label: 'Settings', to: '/orgs/:orgSlug/settings' },
  },
});

const settingsIndexRoute = createRoute({
  path: '/',
  getParentRoute: () => settingsLayoutRoute,
  beforeLoad: ({ params }) => {
    throw redirect({
      to: '/orgs/$orgSlug/settings/api-keys',
      params: { orgSlug: params.orgSlug },
    });
  },
});

const settingsTeamRoute = createRoute({
  path: 'team',
  getParentRoute: () => settingsLayoutRoute,
  component: TeamSettingsPage,
  staticData: { crumb: { kind: 'static', label: 'Team' } },
});

const settingsApiKeysRoute = createRoute({
  path: 'api-keys',
  getParentRoute: () => settingsLayoutRoute,
  component: ApiKeysSettingsPage,
  staticData: { crumb: { kind: 'static', label: 'API keys' } },
});

const settingsBrandingRoute = createRoute({
  path: 'branding',
  getParentRoute: () => settingsLayoutRoute,
  component: BrandingSettingsPage,
  staticData: { crumb: { kind: 'static', label: 'Branding' } },
});

const settingsWebhooksRoute = createRoute({
  path: 'webhooks',
  getParentRoute: () => settingsLayoutRoute,
  component: WebhooksSettingsPage,
  staticData: { crumb: { kind: 'static', label: 'Webhooks' } },
});

const settingsDomainsRoute = createRoute({
  path: 'domains',
  getParentRoute: () => settingsLayoutRoute,
  component: DomainsSettingsPage,
  staticData: { crumb: { kind: 'static', label: 'Custom domains' } },
});

const settingsRouteTree = settingsLayoutRoute.addChildren([
  settingsIndexRoute,
  settingsTeamRoute,
  settingsApiKeysRoute,
  settingsBrandingRoute,
  settingsWebhooksRoute,
  settingsDomainsRoute,
]);

const orgRouteTree = orgRoute.addChildren([
  sessionsRoute,
  sessionDetailRoute,
  templateCreateRoute,
  templatesRoute,
  templateDetailRouteTree,
  rubricsRoute,
  rubricCreateRoute,
  rubricDetailRouteTree,
  extractionsRoute,
  extractionCreateRoute,
  extractionDetailRouteTree,
  candidatesIndexRoute,
  candidateDetailRoute,
  settingsRouteTree,
]);

const orgsRouteTree = orgsLayoutRoute.addChildren([orgsIndexRoute, orgRouteTree]);

const authenticatedRouteTree = authenticatedRoute.addChildren([
  homeRedirectRoute,
  orgsRouteTree,
  adminRouteTree,
]);

const routeTree = rootRoute.addChildren([oauthConsentRoute, authRouteTree, authenticatedRouteTree]);

export { routeTree };
